"use client";

import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar, Clock, ChevronRight, Pencil, Trash2 } from "lucide-react";
import appointmentQueryKeys from "@/app/(authenticated)/appointments/_modules/services/keys";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { Card, CardContent, CardHeader } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { NewAppointmentDialog } from "@/app/(authenticated)/appointments/_modules/components/NewAppointmentDialog";
import { ConfirmDeleteModal } from "@/app/(authenticated)/appointments/_modules/components/confirmDelete";
import { useDeleteAppointment } from "@/app/(authenticated)/appointments/_modules/hooks/queries";
import { getStatusDisplayName } from "@/app/(authenticated)/appointments/_modules/util/helpers";
import { statusColors } from "@/app/(authenticated)/appointments/_modules/constants/statusColors";
import { formatTimeWithTimezone } from "@/app/(authenticated)/appointments/_modules/util/timezone";
import type { TStatus } from "@/app/_module/types/filter-types";

interface LeadAppointmentsSummaryProps {
  leadId: string;
  leadState?: string;
  isEditing?: boolean;
  onNavigateToHistory?: () => void;
}

interface Appointment {
  id: string;
  title: string;
  description?: string;
  notes?: string;
  startTime: string;
  endTime: string;
  status: string;
  type?: string;
  location?: string;
  meetingLink?: string;
}

const MAX_VISIBLE = 3;

export const LeadAppointmentsSummary = ({ leadId, leadState, isEditing, onNavigateToHistory }: LeadAppointmentsSummaryProps) => {
  const queryClient = useQueryClient();
  const [editingAppointment, setEditingAppointment] = useState<Appointment | undefined>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingAppointmentId, setDeletingAppointmentId] = useState<string | null>(null);
  const deleteAppointmentMutation = useDeleteAppointment();

  const { data, isLoading } = useQuery({
    queryKey: appointmentQueryKeys.byLead(leadId),
    queryFn: async () => {
      const baseUrl = getApiUrl(endpoint.appointments.getAppointments);
      const params = new URLSearchParams({
        entityType: 'Lead',
        entityId: leadId,
        page: '1',
        limit: '50'
      });
      const response = await api.get({
        url: `${baseUrl}?${params.toString()}`,
        auth: true,
      });
      return (response as any)?.items || [];
    },
    staleTime: 5 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  const rawAppointments = (Array.isArray(data) ? data : []) as Appointment[];

  // Split into upcoming and past, sorted appropriately
  const { upcoming, pastCount, totalCount } = useMemo(() => {
    const now = Date.now();
    const upcomingList: Appointment[] = [];
    let past = 0;

    for (const apt of rawAppointments) {
      if (new Date(apt.startTime).getTime() >= now) {
        upcomingList.push(apt);
      } else {
        past++;
      }
    }

    // Sort upcoming: closest first
    upcomingList.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

    return { upcoming: upcomingList, pastCount: past, totalCount: rawAppointments.length };
  }, [rawAppointments]);

  const handleEditClick = (appointment: Appointment) => {
    setEditingAppointment(appointment);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingAppointment(undefined);
  };

  const handleDeleteClick = (appointmentId: string) => {
    setDeletingAppointmentId(appointmentId);
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deletingAppointmentId) {
      deleteAppointmentMutation.mutate(deletingAppointmentId, {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: appointmentQueryKeys.byLead(leadId) });
        },
      });
    }
    setConfirmDeleteOpen(false);
    setDeletingAppointmentId(null);
  };

  // Don't render anything while loading or if there are no appointments at all
  if (isLoading || totalCount === 0) {
    return null;
  }

  return (
    <>
      <Card className="shadow-sm border-gray-200">
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Calendar className="h-5 w-5 text-primary" />
            Appointments
            <span className="ml-1 px-2 py-0.5 text-sm font-medium bg-primary/10 text-primary rounded">
              {totalCount}
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-0">
            {upcoming.length === 0 ? (
              <p className="text-sm text-gray-500 py-3">No upcoming appointments</p>
            ) : (
              upcoming.slice(0, MAX_VISIBLE).map((appointment) => {
                const tzInfo = formatTimeWithTimezone(appointment.startTime, leadState);
                return (
                  <div
                    key={appointment.id}
                    className="flex items-start justify-between py-3 border-b last:border-b-0"
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-1.5 rounded-full bg-primary/10 mt-0.5 shrink-0">
                        <Calendar className="h-3.5 w-3.5 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {appointment.title || 'Appointment'}
                        </p>
                        {tzInfo && (
                          <div className="flex items-center gap-1 mt-0.5">
                            <Clock className="h-3 w-3 text-gray-400 shrink-0" />
                            {tzInfo.sameTimezone || !tzInfo.clientTime ? (
                              <span className="text-xs text-gray-500">{tzInfo.agentTime}</span>
                            ) : (
                              <span className="text-xs text-gray-500">
                                <span className="font-medium">You:</span> {tzInfo.agentTime}{' '}
                                <span className="text-gray-400">({tzInfo.agentTz})</span>
                                <span className="text-gray-300 mx-1">|</span>
                                <span className="font-medium">Client:</span> {tzInfo.clientTime}{' '}
                                <span className="text-gray-400">({tzInfo.clientTz})</span>
                              </span>
                            )}
                          </div>
                        )}
                        {appointment.type && (
                          <p className="text-xs text-gray-400 mt-0.5 capitalize">
                            {appointment.type.replace(/-/g, ' ')}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <Badge
                        variant="outline"
                        className={`text-xs ${statusColors[appointment.status || 'pending'] || statusColors.pending}`}
                      >
                        {getStatusDisplayName((appointment.status as TStatus) || 'pending')}
                      </Badge>
                      {isEditing && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditClick(appointment)}
                            className="h-6 w-6 p-0"
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteClick(appointment.id)}
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            {/* Footer: show count of remaining + link to History */}
            {(upcoming.length > MAX_VISIBLE || pastCount > 0) && (
              <button
                onClick={onNavigateToHistory}
                className="flex items-center justify-center gap-1 w-full pt-3 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                {upcoming.length > MAX_VISIBLE
                  ? `View all ${upcoming.length} upcoming`
                  : 'View all'
                }
                {pastCount > 0 && ` + ${pastCount} past`}
                <ChevronRight className="h-3 w-3" />
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      <NewAppointmentDialog
        open={isDialogOpen}
        onOpenChange={handleCloseDialog}
        editingAppointment={editingAppointment as any}
      />

      <ConfirmDeleteModal
        message="Are you sure you want to delete this appointment? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onOpenChange={setConfirmDeleteOpen}
        open={confirmDeleteOpen}
        title="Delete Appointment"
      />
    </>
  );
};
